<template>
    <HeadlessDialog :open="Boolean(newApiKey)"
                    @close="handleApiKeyModalClose"
                    class="relative z-[80]">
        <div class="fixed inset-0 bg-black/50" aria-hidden="true"/>
        <div class="fixed inset-0 flex w-screen items-center justify-center p-4">
            <DialogPanel class="w-full max-w-sm rounded bg-white p-4 dark:bg-neutral-900">
                <DialogTitle class="mb-3 text-center text-2xl font-semibold">
                    Copy Your API Key
                </DialogTitle>
                <DialogDescription class="mb-4 text-sm text-neutral-500">
                    This key will only be shown once. Copy it before closing this window.
                </DialogDescription>
                <FakeInput click-to-copy>
                    <span class="block break-all font-mono text-xs">{{ newApiKey?.token }}</span>
                </FakeInput>
                <div class="mt-5 flex justify-end">
                    <Button primary @click="handleApiKeyModalClose">
                        Done
                    </Button>
                </div>
            </DialogPanel>
        </div>
    </HeadlessDialog>

    <Loading v-if="loading"/>
    <Page>
        <Container>
            <div class="mx-auto max-w-sm">

                <Title class="mb-6">Profile</Title>

                <Field>
                    <Label>Profile Photo</Label>
                    <FileInput v-model="form.fields.fileId"/>
                    <FormError :form="form" field="fileId"/>
                </Field>

                <Field>
                    <Label>First Name *</Label>
                    <TextInput v-model="form.fields.firstName" placeholder="First Name"/>
                    <FormError :form="form" field="firstName"/>
                </Field>

                <Field>
                    <Label>Last Name *</Label>
                    <TextInput v-model="form.fields.lastName" placeholder="Last Name"/>
                    <FormError :form="form" field="lastName"/>
                </Field>

                <Field>
                    <Label>Display Name</Label>
                    <TextInput v-model="form.fields.displayName" placeholder="Shown in Marketplace"/>
                    <FormError :form="form" field="displayName"/>
                    <Help class="mt-2">
                        This is shown publicly when you publish Marketplace resources.
                    </Help>
                </Field>

                <Field>
                    <Label>Email *</Label>
                    <TextInput v-model="form.fields.email" placeholder="Email"/>
                    <FormError :form="form" field="email"/>
                </Field>

                <Field v-if="false">
                    <Label>Username *</Label>
                    <TextInput v-model="form.fields.username" placeholder="Username"/>
                    <FormError :form="form" field="username"/>
                    <Help class="mt-2">
                        <a class="underline text-brand-primary-800 dark:text-brand-primary-400" target="_blank"
                           :href="profileUrl">{{ profileUrl }}</a>
                    </Help>
                </Field>

                <Field>
                    <Label>Birth Date</Label>
                    <DateInput v-model="form.fields.birthDate" placeholder="Birth Date"/>
                    <FormError :form="form" field="birthDate"/>
                </Field>

                <Field>
                    <Label>Start Date</Label>
                    <DateInput v-model="form.fields.startDate" placeholder="Start Date"/>
                    <FormError :form="form" field="startDate"/>
                </Field>

                <Field>
                    <Label>Timezone (Search by nearest Major City)</Label>
                    <TimezoneInput v-model="form.fields.timezone"/>
                    <FormError :form="form" field="timezone"/>
                </Field>

                <Field>
                    <Label>System of Measure</Label>
                    <SelectInput :options="systemOfMeasureOptions" v-model="form.fields.systemOfMeasure"/>
                    <FormError :form="form" field="timezone"/>
                </Field>

                <Field>
                    <Box data-testid="api-key-section">
                        <div class="flex items-center justify-between gap-3">
                            <div>
                                <Label>API Keys</Label>
                                <Help>Create keys for personal integrations and scripts.</Help>
                                <a :href="apiDocsUrl"
                                   class="mt-1 inline-block text-xs text-brand-primary-400 hover:underline">
                                    View API documentation
                                </a>
                            </div>
                            <Tooltip label="Add API Key" v-if="!apiKeyFormActive">
                                <button type="button"
                                        aria-label="Add API Key"
                                        class="flex h-8 w-8 items-center justify-center rounded border border-neutral-500"
                                        @click="handleApiKeyFormShow">
                                    <Icon fixedWidth icon="plus"/>
                                </button>
                            </Tooltip>
                        </div>

                        <div v-if="apiKeyFormActive" class="mt-4">
                            <div>
                                <TextInput v-model="apiKeyForm.fields.name"
                                           placeholder="Key name"
                                           @keydown.enter="handleApiKeyCreate"/>
                                <FormError :form="apiKeyForm" field="name"/>
                            </div>
                            <div class="mt-3 flex items-center justify-end gap-2">
                                <Button secondary outlined
                                        :disabled="apiKeysLoading"
                                        @click="handleApiKeyFormCancel">
                                    Cancel
                                </Button>
                                <Button primary
                                        :disabled="apiKeysLoading"
                                        @click="handleApiKeyCreate">
                                    Save
                                </Button>
                            </div>
                        </div>

                        <div v-if="apiKeys.length" class="mt-4 border-t border-neutral-200 dark:border-neutral-700">
                            <div v-for="apiKey in apiKeys"
                                 :key="apiKey.id"
                                 class="flex min-h-14 items-center gap-3 border-b border-neutral-200 py-3 last:border-b-0 dark:border-neutral-700">
                                <div class="min-w-0 flex-1">
                                    <div class="truncate font-semibold">{{ apiKey.name }}</div>
                                    <div class="truncate text-xs text-neutral-500">
                                        Created {{ formatApiKeyDate(apiKey.created_at) }}
                                        <template v-if="apiKey.last_used_at">
                                            · Used {{ formatApiKeyDate(apiKey.last_used_at) }}
                                        </template>
                                    </div>
                                </div>
                                <Button danger outlined size="xs" @click="handleApiKeyRevoke(apiKey)">
                                    Revoke
                                </Button>
                            </div>
                        </div>

                        <Help v-else-if="!apiKeysLoading && !apiKeyFormActive" class="mt-4">
                            No API keys yet.
                        </Help>
                    </Box>
                </Field>

                <Field v-if="user">

                    <Box>

                        <Label>X3 Log Connection</Label>

                        <Button secondary outlined block v-if="user.isV2()" @click="handleX3LogDisconnect">
                            Disconnect from X3 Log
                        </Button>

                        <Button secondary outlined block v-else @click="handleX3LogFormShow" v-if="!x3LogFormActive">
                            Connect to X3 Log
                        </Button>

                        <div v-if="user.isV2()" class="my-3">
                            <Button primary outlined block @click="handleX3LogImport">
                                Import X3 Log Data
                            </Button>
                        </div>

                        <template v-if="x3LogFormActive">

                            <Subtitle>X3 Log Credentials</Subtitle>

                            <Field>
                                <Label>Email</Label>
                                <TextInput v-model="x3LogForm.fields.email"/>
                            </Field>

                            <Field>
                                <Label>Password</Label>
                                <PasswordInput v-model="x3LogForm.fields.password"/>
                            </Field>

                            <div class="flex items-center justify-end gap-x-4">
                                <Button @click="handleX3LogCancel">
                                    Cancel
                                </Button>
                                <Button primary @click="handleX3LogConnect">
                                    Connect
                                </Button>
                            </div>

                        </template>

                    </Box>

                </Field>

                <Field v-if="false">
                    <Label>Public *</Label>
                    <div class="flex items-center justify-between">
                        <div>
                            Allow your profile to be viewed by others.
                        </div>
                        <Switch v-model="form.fields.public"></Switch>
                    </div>

                    <FormError :form="form" field="public"/>
                </Field>

                <Controls>
                    <template v-slot:left>
                        <Button danger outlined @click="handleUserDelete">
                            Delete
                        </Button>
                    </template>
                    <template v-slot:right>
                        <Button primary @click="handleUserSave">
                            Save
                        </Button>
                    </template>
                </Controls>

            </div>
        </Container>
    </Page>
</template>

<script>
import {defineComponent} from 'vue'
import {Form} from '@/internal.js';
import {mapActions} from 'vuex';
import SelectInput from '@/components/SelectInput.vue';
import FakeInput from '@/components/FakeInput.vue';
import Paragraph from '@/components/Paragraph.vue';
import TextInput from '@/components/TextInput.vue';
import PasswordInput from '@/components/PasswordInput.vue';
import {capturePostHogEvent, resetPostHog} from '@/utils/posthog';
import {
  Dialog as HeadlessDialog,
  DialogDescription,
  DialogPanel,
  DialogTitle,
} from '@headlessui/vue';

export default defineComponent({
  name: 'Profile',
  components: {
    DialogDescription,
    DialogPanel,
    DialogTitle,
    FakeInput,
    HeadlessDialog,
    Paragraph,
    PasswordInput,
    SelectInput,
    TextInput,
  },
  computed: {
    apiDocsUrl() {
      return `${window.app.env.apiBaseUrl}/docs`;
    },
    profileUrl() {
      return `${window.app.env.appBaseUrl}/u/${this.form.fields.username}`;
    },
    systemOfMeasureOptions() {
      return [
        {
          label: 'Imperial',
          value: 'imperial',
        },
        {
          label: 'Metric',
          value: 'metric',
        },
      ];
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  created() {
    this.loadUser();
    this.loadApiKeys();
    window.app.on('fileUpdated', this.fileUpdateCheck)
  },
  destroyed() {
    window.app.off('fileUpdated', this.fileUpdateCheck)
  },
  data() {
    return {
      file: null,
      fileUpdate: false,
      apiKeyForm: new Form({
        name: '',
      }),
      apiKeyFormActive: false,
      apiKeys: [],
      apiKeysLoading: false,
      form: new Form({
        birthDate: null,
        displayName: '',
        email: '',
        fileId: null,
        firstName: '',
        lastName: '',
        public: false,
        startDate: null,
        systemOfMeasure: 'imperial',
        timezone: '',
        username: '',
      }),
      loading: false,
      newApiKey: null,
      x3LogForm: new Form({
        email: '',
        password: '',
      }),
      x3LogFormActive: false,
    };
  },
  methods: {
    ...mapActions({
      apiKeyCreate: 'my/apiKeyCreate',
      apiKeyDestroy: 'my/apiKeyDestroy',
      apiKeyIndex: 'my/apiKeyIndex',
      authUserShow: 'my/show',
      authUserUpdate: 'my/update',
      authUserDestroy: 'my/destroy',
      authUserX3LogConnect: 'my/x3LogConnect',
      authUserX3LogDisconnect: 'my/x3LogDisconnect',
      uiFileDialog: 'ui/imageEditor',
    }),
    formatApiKeyDate(value) {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
      }).format(new Date(value));
    },
    handleApiKeyCreate() {
      if (this.apiKeysLoading) {
        return;
      }

      this.apiKeyForm.errors.clear();
      this.apiKeysLoading = true;

      return this.apiKeyCreate({
        ...this.apiKeyForm.fields,
      }).then((response) => {
        this.newApiKey = response.data.data;
        this.apiKeyForm.fields.name = '';
        this.apiKeyFormActive = false;
        return this.loadApiKeys();
      }).catch((error) => {
        this.apiKeyForm.recordErrors(error);
        window.app.snackbarError(error);
      }).finally(() => {
        this.apiKeysLoading = false;
      });
    },
    handleApiKeyFormCancel() {
      this.apiKeyForm.errors.clear();
      this.apiKeyForm.fields.name = '';
      this.apiKeyFormActive = false;
    },
    handleApiKeyFormShow() {
      this.apiKeyFormActive = true;
    },
    handleApiKeyModalClose() {
      this.newApiKey = null;
    },
    handleApiKeyRevoke(apiKey) {
      window.app.dialog({
        title: 'Revoke API Key',
        message: `Revoke “${apiKey.name}”? Anything using this key will immediately lose access.`,
        actions: [
          {
            text: 'Cancel',
            outlined: true,
            click: () => {},
          },
          {
            text: 'Revoke',
            danger: true,
            click: () => {
              this.apiKeysLoading = true;
              this.apiKeyDestroy({
                apiKeyId: apiKey.id,
              }).then(() => {
                if (this.newApiKey?.id === apiKey.id) {
                  this.newApiKey = null;
                }
                window.app.snackbar({
                  message: 'API Key Revoked',
                });
                return this.loadApiKeys();
              }).catch((error) => {
                window.app.snackbarError(error);
              }).finally(() => {
                this.apiKeysLoading = false;
              });
            },
          },
        ],
      });
    },
    fileUpdateCheck(payload = {}) {
      if (this.form.fields.fileId === payload.fileId) {
        this.loadUser();
      }
    },
    fillForm() {
      this.form.fields = {
        birthDate: this.user.birthDate ? this.user.birthDate.format('YYYY-MM-DD') : null,
        displayName: this.user.displayName,
        email: this.user.email,
        fileId: this.user.file ? this.user.file.id : null,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        public: this.user.public,
        startDate: this.user.startDate ? this.user.startDate.format('YYYY-MM-DD') : null,
        systemOfMeasure: this.user.systemOfMeasure,
        timezone: this.user.timezone,
        username: this.user.username,
      };
    },
    handleUserDelete() {

      if (this.loading) {
        return;
      }

      window.app.dialog({
        title: 'Delete Account',
        message: `Are you absolutely sure you want to delete your account? There's no ability to undo this action. All your data will be deleted permanently.`,
        actions: [
          {
            text: 'Cancel',
            outlined: true,
            click: () => {
            },
          },
          {
            text: 'Delete My Account',
            danger: true,
            click: () => {

              this.loading = true;
              this.authUserDestroy().then(() => {

                capturePostHogEvent('account_deleted');
                resetPostHog();

                window.app.snackbar({
                  message: `Your account has been deleted`,
                });

                this.$router.push(window.app.findRouteByName('auth/sign-up'));
              }).finally(() => {
                this.loading = false;
              });

            },
          }
        ],
      });

    },
    handleUserSave() {

      if (this.loading) {
        return;
      }

      this.loading = true;

      this.form.errors.clear();

      return this.authUserUpdate({
        ...this.form.fields,
      }).then(() => {
        window.app.snackbar({
          message: 'Profile Updated',
          type: 'success',
        });
      }).catch((error) => {
        this.form.recordErrors(error);
        window.app.snackbarError(error);
      }).finally(() => {
        this.fileUpdate = false;
        this.loading = false;
      });
    },
    handleX3LogCancel() {
      this.x3LogFormActive = false;
    },
    handleX3LogConnect() {
      this.loading = true;
      this.authUserX3LogConnect({
        ...this.x3LogForm.fields,
      }).then(() => {
        window.app.snackbar({
          message: 'X3 Log Connected',
        });
        this.x3LogFormActive = false;
      }).catch((error) => {
        window.app.snackbarError(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    handleX3LogDisconnect() {
      this.loading = true;
      this.authUserX3LogDisconnect().then(() => {
        window.app.snackbar({
          message: 'X3 Log Disconnected',
        });
      }).catch((error) => {
        window.app.snackbarError(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    handleX3LogFormShow() {
      this.x3LogFormActive = true;
    },
    handleX3LogImport() {
      this.$router.push(window.app.findRouteByName('my/import'));
    },
    loadUser() {
      this.loading = true;
      return this.authUserShow({
        params: {
          include: [
            'file',
          ]
        }
      }).then(this.fillForm).finally(() => {
        this.loading = false;
      });
    },
    loadApiKeys() {
      this.apiKeysLoading = true;
      return this.apiKeyIndex().then((response) => {
        this.apiKeys = response.data.data;
      }).catch((error) => {
        window.app.snackbarError(error);
      }).finally(() => {
        this.apiKeysLoading = false;
      });
    },
  },
  watch: {
    ['form.fields.fileId']: {
      handler() {
        this.handleUserSave();
      },
    }
  },
})
</script>

<style scoped>

</style>
